import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, User, ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { getBlogPostBySlug, getRelatedPosts } from '../lib/blog';
import { RelatedPosts } from '../components/RelatedPosts';
import { GlassCTA } from '../components/GlassCTA';
import { ShareButtons } from '../components/ShareButtons';
import { RotatingCallout } from '../components/RotatingCallout';
import { BackToTop } from '../components/BackToTop';

export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState<any>(null);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    if (slug) {
      getBlogPostBySlug(slug).then(data => {
        if (data) {
          setPost(data);
          getRelatedPosts(data.meta.slug, data.meta.category).then(setRelatedPosts);
        }
      });
    }
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-950 text-yellow-100 py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Loading blog post...</h1>
        <p className="text-yellow-300">Please wait while we fetch the content.</p>
      </div>
    );
  }

  const { meta, Content } = post;
  const canonicalUrl = `https://propviper.com/blog/${meta.slug}`;
  const imageUrl = meta.image?.startsWith('http')
    ? meta.image
    : `https://propviper.com${meta.image || '/default-og-image.jpg'}`;

  return (
    <>
      <Helmet>
        <title>{`${meta.title} | PropViper Trading Blog`}</title>
        <meta name="description" content={meta.excerpt} />
        <meta name="author" content={meta.author} />
        <meta name="keywords" content={`prop trading, ${meta.category.toLowerCase()}, trading tips, prop firm guide`} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.excerpt} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="PropViper" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.excerpt} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:site" content="@thepropviper" />
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-yellow-100">
        <div className="container mx-auto px-4 py-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Link to="/blog" className="inline-flex items-center text-yellow-300 hover:text-yellow-400 font-medium">
                <ArrowLeft className="mr-2 w-4 h-4" /> Back to Blog
              </Link>
            </div>

            <div className="bg-white/5 backdrop-blur-lg rounded-xl shadow-lg overflow-hidden mb-10">
              {meta.image ? (
                <img
                  src={meta.image}
                  alt={meta.title}
                  className="w-full h-64 sm:h-96 object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-64 sm:h-96 bg-yellow-900 flex items-center justify-center text-yellow-100 text-xl">
                  Image not available
                </div>
              )}

              <div className="p-6 sm:p-8">
                <div className="flex flex-wrap items-center gap-3 text-sm text-yellow-300 mb-3">
                  <span className="bg-yellow-800/30 px-3 py-1 rounded-full font-medium">
                    {meta.category}
                  </span>
                  <span>{meta.date}</span>
                  <span className="flex items-center gap-1"><User className="w-4 h-4" />{meta.author}</span>
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{meta.readTime}</span>
                </div>
                <h1 className="text-3xl sm:text-4xl font-extrabold text-yellow-100 mb-4">
                  {meta.title}
                </h1>
                <p className="text-yellow-400 text-base sm:text-lg mb-4">{meta.excerpt}</p>

                <RotatingCallout title={meta.title} url={canonicalUrl} />
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-lg p-6 sm:p-8 rounded-xl shadow-lg">
              <GlassCTA
                variant="primary"
                position="top"
                promoUsedElsewhere={false}
                detectFirmFromTitle={meta.title}
              />

              <div className="overflow-x-auto">
                <div className="prose prose-lg prose-invert max-w-prose mx-auto">
                  {Content ? <Content /> : <p className="text-red-400">No content found in MDX default export</p>}
                </div>
              </div>

              <div className="mt-20">
                <GlassCTA
                  variant="secondary"
                  position="bottom"
                  promoUsedElsewhere={true}
                  detectFirmFromTitle={meta.title}
                />
              </div>

              {relatedPosts.length > 0 && (
                <div className="mt-16">
                  <RelatedPosts posts={relatedPosts} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <BackToTop />
    </>
  );
}
